
import { supabase } from "@/integrations/supabase/client";
import { BookingInfo } from "@/types/booking";

/**
 * Creates a booking record in the database
 */
export const createBookingRecord = async (
  bookingInfo: BookingInfo,
  selectedDate: Date,
  selectedTime: string,
  userId: string,
  bookingId?: string
) => {
  console.log("=== CREATE BOOKING RECORD START ===");
  console.log("Parameters received:", {
    bookingInfo: {
      practitionerId: bookingInfo?.practitionerId,
      serviceId: bookingInfo?.serviceId,
      price: bookingInfo?.price,
      currency: bookingInfo?.currency,
      duration: bookingInfo?.duration,
      format: bookingInfo?.format
    },
    selectedDate,
    selectedTime,
    userId,
    bookingId
  });

  if (!bookingInfo) {
    console.error("ERROR: Missing booking information");
    throw new Error("Missing booking information");
  }

  if (!userId) {
    console.error("ERROR: User is not logged in");
    throw new Error("User is not logged in");
  }

  if (!selectedDate || !selectedTime) {
    console.error("ERROR: Missing date/time selection", { selectedDate, selectedTime });
    throw new Error("Please select both date and time for your appointment");
  }

  if (userId === bookingInfo.practitionerId) {
    console.error("ERROR: User trying to book own service", { userId, practitionerId: bookingInfo.practitionerId });
    throw new Error("You cannot book your own services. Please use a client account to book services.");
  }

  // Validate required booking info fields
  if (!bookingInfo.practitionerId || !bookingInfo.serviceId) {
    console.error("ERROR: Missing required booking IDs", {
      practitionerId: bookingInfo.practitionerId,
      serviceId: bookingInfo.serviceId
    });
    throw new Error("Missing required booking information (practitioner or service ID)");
  }

  // Allow zero-price (free) services but require currency and duration
  const hasValidPrice = bookingInfo.price !== undefined && bookingInfo.price !== null;
  if (!hasValidPrice || !bookingInfo.currency || !bookingInfo.duration) {
    console.error("ERROR: Missing booking details", {
      price: bookingInfo.price,
      currency: bookingInfo.currency,
      duration: bookingInfo.duration
    });
    throw new Error("Missing required booking details (price can be 0, but currency and duration are required)");
  }

  const [hours, minutes] = selectedTime.split(':').map(Number);
  const startTime = new Date(selectedDate);
  startTime.setHours(hours, minutes, 0, 0);
  
  const endTime = new Date(startTime.getTime() + (bookingInfo.duration * 60000));

  const startTimeIso = startTime.toISOString();
  const endTimeIso = endTime.toISOString();

  const sessionFormat = bookingInfo.format || 'online';

  // Enhanced conflict checking logic - exclude expired pending payments
  console.log("Checking for booking conflicts...");
  console.log("Start time:", startTimeIso);
  console.log("End time:", endTimeIso);
  
  try {
    // First, trigger cleanup of expired bookings by calling the database function
    const { error: cleanupError } = await supabase.rpc('cleanup_expired_unpaid_bookings');
    if (cleanupError) {
      console.warn("Cleanup function error (non-fatal):", cleanupError);
    }

    const { data: existingBookings, error: conflictCheckError } = await supabase
      .from('bookings')
      .select('id, start_time, end_time, status, created_at')
      .eq('practitioner_id', bookingInfo.practitionerId)
      .in('status', ['Scheduled', 'Confirmed', 'Pending Payment']);

    if (conflictCheckError) {
      console.error("Error checking for booking conflicts:", conflictCheckError);
      throw new Error(`Failed to check availability: ${conflictCheckError.message}`);
    }

    console.log(`Found ${existingBookings?.length || 0} existing bookings to check for conflicts`);
    
    // Enhanced conflict filtering - exclude old pending payments
    const conflicts = existingBookings?.filter(booking => {
      const bookingStart = new Date(booking.start_time);
      const bookingEnd = new Date(booking.end_time);
      const bookingCreated = new Date(booking.created_at);
      
      // Skip old pending payment bookings (older than 25 minutes)
      if (booking.status === 'Pending Payment') {
        const minutesSinceCreated = (Date.now() - bookingCreated.getTime()) / (1000 * 60);
        if (minutesSinceCreated > 25) {
          console.log(`Ignoring old pending payment booking ${booking.id} (${minutesSinceCreated.toFixed(1)} minutes old)`);
          return false;
        }
      }
      
      // Check for actual overlap
      const hasOverlap = (startTime < bookingEnd && endTime > bookingStart);
      
      if (hasOverlap) {
        console.log(`Conflict detected with booking ${booking.id} (${booking.status}):`, {
          bookingStart: booking.start_time,
          bookingEnd: booking.end_time,
          created: booking.created_at
        });
      }
      
      return hasOverlap;
    });

    // Only throw an error if there are actual conflicts
    if (conflicts && conflicts.length > 0) {
      console.error(`Time slot conflict detected: ${conflicts.length} conflicts found`);
      throw new Error("This time slot is no longer available. Please select a different time.");
    }

    console.log("No conflicts found, proceeding with booking creation");

    // Fetch session instructions from the service to snapshot into the booking (privacy-protecting)
    let serviceInstructions: string | null = null;
    try {
      const { data: svc, error: svcErr } = await supabase
        .from('services')
        .select('session_instructions')
        .eq('id', bookingInfo.serviceId)
        .single();
      if (svcErr) {
        console.warn('Could not fetch service session_instructions (non-fatal):', svcErr.message);
      } else {
        serviceInstructions = (svc as any)?.session_instructions || null;
      }
    } catch (e) {
      console.warn('Error fetching service session_instructions (non-fatal):', (e as any)?.message);
    }

    // Calculate confirmation deadline (48 hours from now or start_time, whichever comes first)
    const now = new Date();
    const fortyEightHoursFromNow = new Date(now.getTime() + 48 * 60 * 60 * 1000);
    const confirmationDeadline = startTime < fortyEightHoursFromNow ? startTime : fortyEightHoursFromNow;

    const bookingData = {
      ...(bookingId && { id: bookingId }),
      client_id: userId,
      practitioner_id: bookingInfo.practitionerId,
      service_id: bookingInfo.serviceId,
      price_at_booking: bookingInfo.price,
      currency_at_booking: bookingInfo.currency,
      status: 'Pending Payment',
      payment_status: 'Pending',
      start_time: startTimeIso,
      end_time: endTimeIso,
      format: sessionFormat,
      session_instructions: serviceInstructions,
      confirmation_deadline: confirmationDeadline.toISOString()
    };

    console.log("Creating booking record with data:", bookingData);

    console.log("=== ATTEMPTING DATABASE INSERT ===");
    console.log("Final booking data being inserted:", JSON.stringify(bookingData, null, 2));

    const { data: newBooking, error } = await supabase
      .from('bookings')
      .insert(bookingData)
      .select()
      .single();

    console.log("=== DATABASE INSERT RESPONSE ===");
    console.log("Insert result data:", newBooking);
    console.log("Insert result error:", error);

    if (error) {
      console.error("=== DATABASE INSERT ERROR ===");
      console.error("Full error object:", JSON.stringify(error, null, 2));
      console.error("Error code:", error.code);
      console.error("Error message:", error.message);
      console.error("Error details:", error.details);
      console.error("Error hint:", error.hint);
      
      // Check if the error is due to a time slot conflict
      if (error.message.includes("violates")) {
        throw new Error("This time slot is no longer available. Please select a different time.");
      }
      
      // Check for RLS (Row Level Security) policy violation
      if (error.message.includes("new row violates row-level security policy")) {
        console.error("RLS Policy violation detected. User:", userId);
        throw new Error("Permission denied: Unable to create booking. Please ensure you are logged in properly.");
      }
      
      throw new Error(`Failed to create booking: ${error.message} (Code: ${error.code})`);
    }

    if (!newBooking || !newBooking.id) {
      console.error("=== UNEXPECTED RESULT ===");
      console.error("No booking data returned but no error reported");
      throw new Error("Booking was created but no ID was returned");
    }

    console.log("=== BOOKING CREATED SUCCESSFULLY ===");
    console.log("New booking created:", JSON.stringify(newBooking, null, 2));
    console.log("Returned booking ID:", newBooking.id);
    console.log("=== CREATE BOOKING RECORD END ===");
    
    return newBooking.id;
  } catch (err) {
    console.error("Error in createBookingRecord:", err);
    throw err; // Re-throw to be handled by the caller
  }
};
